import { CANVAS_SIZE } from './canvas-utils';
import * as htmlToImage from 'html-to-image';

/**
 * Generate a free, low-resolution, WATERMARKED preview of the current canvas.
 * Returns a dataURL ready to download.
 *
 * @param node - DOM element to capture (the canvas root)
 * @param options.format 'png' | 'jpg'
 * @param options.previewSize px size of the preview output (default 512)
 * @param options.bgFill optional fill color (used for JPG)
 */
export async function exportFreePreview(node, options = {}) {
  const { format = 'png', previewSize = 512, bgFill } = options;
  const rect = node.getBoundingClientRect();
  // Render at preview resolution
  const pixelRatio = previewSize / Math.max(1, rect.width);
  const baseOpts = {
    pixelRatio,
    cacheBust: true,
    width: rect.width,
    height: rect.height,
    skipFonts: true,
  };

  let dataUrl;
  if (format === 'png') {
    dataUrl = await htmlToImage.toPng(node, baseOpts);
  } else {
    dataUrl = await htmlToImage.toJpeg(node, {
      ...baseOpts,
      quality: 0.85,
      backgroundColor: bgFill || '#ffffff',
    });
  }

  // Overlay watermark via canvas2d
  const img = await loadImg(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');

  if (format === 'jpg') {
    ctx.fillStyle = bgFill || '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(img, 0, 0);

  // Diagonal tiled watermark
  ctx.save();
  const fontSize = Math.max(18, Math.round(canvas.width / 22));
  ctx.font = `800 ${fontSize}px Outfit, system-ui, sans-serif`;
  ctx.fillStyle = 'rgba(10,10,10,0.18)';
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 1;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(-Math.PI / 7);
  const stepX = fontSize * 9;
  const stepY = fontSize * 4;
  for (let y = -canvas.height; y < canvas.height; y += stepY) {
    for (let x = -canvas.width; x < canvas.width; x += stepX) {
      ctx.strokeText('STCKRSTCKR  ·  PREVIEW', x, y);
      ctx.fillText('STCKRSTCKR  ·  PREVIEW', x, y);
    }
  }
  ctx.restore();

  // Bottom-right small badge
  const badgeText = 'stckrstckr.preview';
  ctx.font = `700 ${Math.max(11, Math.round(canvas.width / 50))}px Outfit, sans-serif`;
  ctx.fillStyle = 'rgba(10,10,10,0.85)';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText(badgeText, canvas.width - 10, canvas.height - 10);

  const mime = format === 'png' ? 'image/png' : 'image/jpeg';
  return canvas.toDataURL(mime, 0.88);
}

function loadImg(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// keep linter happy by exporting referenced const
export { CANVAS_SIZE };
